import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useLiveQuery } from "dexie-react-hooks";
import { nanoid } from "nanoid";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    ChatEntity,
    ChatRole,
    db,
    MessageEntity,
    type Reference,
    type StreamedReference,
} from "src/db";
import { STORAGE_KEYS, useStorage } from "src/hooks/useStorage";
import { scopesNeeded } from "src/utils/authConfig";
import { v4 as uuidv4 } from "uuid";
import "./WidgetChatCustom.css";

type WidgetChatCustomProps = Record<string, never>;

interface StreamEvent {
    event?: string;
    content?: string;
}

interface AnswerJson {
    content: string;
    response_id?: string;
    references?: Reference[];
    tool_call: boolean;
    suggestions?: string[];
}

// Custom icons as SVG components
const SendIcon = () => (
    <svg
        role="img"
        aria-label="Send message"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);

const RobotIcon = () => (
    <svg
        role="img"
        aria-label="AI assistant"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="3" y="11" width="18" height="10" rx="2" ry="2" />
        <circle cx="12" cy="5" r="2" />
        <path d="M12 7v4" />
        <line x1="8" y1="16" x2="8" y2="16" />
        <line x1="16" y1="16" x2="16" y2="16" />
    </svg>
);

const UserIcon = () => (
    <svg
        role="img"
        aria-label="User"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const LinkIcon = () => (
    <svg
        role="img"
        aria-label="External link"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
);

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
    <svg
        role="img"
        aria-label={isOpen ? "Collapse" : "Expand"}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
        }}
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const SparklesIcon = () => (
    <svg
        role="img"
        aria-label="Suggestions"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z" />
    </svg>
);

const CloseIcon = () => (
    <svg
        role="img"
        aria-label="Close"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const ExternalLinkIcon = () => (
    <svg
        role="img"
        aria-label="Open externally"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
);

// Parse content helper
const parseContent = (content: string): unknown => {
    const protectedContent = content.replace(/(\w)'(\w)/g, "$1<APOS>$2");
    const replaced = protectedContent.replace(/'/g, '"');
    const restored = replaced.replace(/<APOS>/g, "'");
    const sanitized = restored
        .replace(/:\s*True/g, ": true")
        .replace(/:\s*False/g, ": false")
        .replace(/:\s*None/g, ": null");

    return JSON.parse(sanitized);
};

// Simple markdown to HTML converter for the widget
const renderMarkdown = (content: string, references?: Reference[]): string => {
    if (!content) return "";

    let html = content;

    // First, handle references to avoid escaping them
    const refMap = new Map<string, number>();
    let refIndex = 1;

    if (references && references.length > 0) {
        // Replace reference IDs with placeholders to protect them
        html = html.replace(/\[([a-zA-Z0-9]{8})\]/g, (match, refId) => {
            const ref = references.find((r) => r.reference_id === refId);
            if (ref) {
                if (!refMap.has(refId)) {
                    refMap.set(refId, refIndex++);
                }
                const displayNumber = refMap.get(refId);
                return `__REF_${refId}_${displayNumber}__`;
            }
            return match;
        });
    }

    // Escape HTML
    html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    // Code blocks (before other processing)
    html = html.replace(/```([^`]+)```/g, "<pre><code>$1</code></pre>");

    // Headers
    html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");
    html = html.replace(/^## (.*$)/gim, "<h2>$1</h2>");
    html = html.replace(/^# (.*$)/gim, "<h1>$1</h1>");

    // Bold (use negative lookahead to avoid matching single asterisks)
    html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

    // Italic (only match single asterisks not part of bold)
    html = html.replace(/(?<!\*)\*(?!\*)([^*]+)(?<!\*)\*(?!\*)/g, "<em>$1</em>");

    // Links
    html = html.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
    );

    // Inline code
    html = html.replace(/`([^`]+)`/g, "<code>$1</code>");

    // Process lists
    const lines = html.split("\n");
    const processedLines = [];
    let inList = false;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const isListItem = line.trim().startsWith("- ") || line.trim().startsWith("* ");

        if (isListItem) {
            if (!inList) {
                processedLines.push("<ul>");
                inList = true;
            }
            const itemContent = line.trim().substring(2);
            processedLines.push(`<li>${itemContent}</li>`);
        } else {
            if (inList && line.trim() === "") {
                // Empty line might be list continuation
                continue;
            }
            if (inList) {
                processedLines.push("</ul>");
                inList = false;
            }

            if (line.trim() !== "") {
                // Check if line is already wrapped in HTML tags
                if (!line.trim().match(/^<[^>]+>/)) {
                    processedLines.push(`<p>${line}</p>`);
                } else {
                    processedLines.push(line);
                }
            }
        }
    }

    if (inList) {
        processedLines.push("</ul>");
    }

    html = processedLines.join("\n");

    // Restore references with proper formatting
    html = html.replace(/__REF_([a-zA-Z0-9]{8})_(\d+)__/g, (_match, refId, displayNumber) => {
        return `<span class="inline-reference" data-ref-id="${refId}">[${displayNumber}]</span>`;
    });

    return html;
};

// Helper to get full URL for references
const getFullReferenceUrl = (source: string): string => {
    if (!source) return "";
    if (source.startsWith("http") || source.startsWith("file://")) {
        return source;
    }

    const backendUrl =
        import.meta.env.VITE_BACKEND_URL || "https://api.ads-deep.mosaic-qa.siemens-energy.cloud";
    const decodedSource = decodeURIComponent(source);

    // Handle special case for storage file URLs
    const finalSource = decodedSource.includes("object_name=/")
        ? decodedSource.replace("object_name=/", "object_name=")
        : decodedSource;

    return `${backendUrl}${finalSource.startsWith("/") ? "" : "/"}${finalSource}`;
};

export function WidgetChatCustom(_props: WidgetChatCustomProps) {
    const { instance, accounts } = useMsal();
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [currentChatId, setCurrentChatId] = useState<string | null>(null);
    const [initialized, setInitialized] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [suggestedMessages, setSuggestedMessages] = useState<string[]>([]);
    const [expandedReferences, setExpandedReferences] = useState<Set<string>>(new Set());
    const [selectedReference, setSelectedReference] = useState<Reference | null>(null);
    const [manualToken, setManualToken] = useState<string | null>(null);
    const [_manualAccount, setManualAccount] = useState<{ name: string } | null>(null);

    // Combined processing state for easier checks
    const _isProcessing = isLoading || isStreaming;

    // Get user preferences
    const [chatPreferences] = useStorage<{
        selectedProductLines: string[];
        selectedDatasources: string[];
    }>(STORAGE_KEYS.CHAT_PREFERENCES, {
        selectedProductLines: [],
        selectedDatasources: [],
    });

    const [selectedOrganization] = useStorage<string>(STORAGE_KEYS.SELECTED_ORGANIZATION, "");

    // Get messages for current chat
    const messages = useLiveQuery(async () => {
        if (!currentChatId) return [];
        try {
            return await db.messages.where("chatId").equals(currentChatId).sortBy("createdAt");
        } catch (error) {
            console.error("Error fetching messages:", error);
            return [];
        }
    }, [currentChatId]);

    // Get ID token for API calls
    const getAccessToken = useCallback(async () => {
        // First check for manual token (from parent web component)
        if (manualToken) {
            return manualToken;
        }

        // Otherwise use MSAL flow
        const account = accounts[0];
        if (!account) throw new Error("No account found");

        try {
            const response = await instance.acquireTokenSilent({
                scopes: scopesNeeded,
                account: account,
            });
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                const response = await instance.acquireTokenPopup({
                    scopes: scopesNeeded,
                    account: account,
                });
                return response.accessToken;
            }
            throw error;
        }
    }, [instance, accounts, manualToken]);

    // Initialize chat on mount or when authentication changes
    useEffect(() => {
        const initChat = async () => {
            // Skip if already initialized AND we have authentication
            if (initialized && (manualToken || accounts.length > 0)) {
                return;
            }

            try {
                // Check if chatId is provided in URL params
                const urlParams = new URLSearchParams(window.location.search);
                const hashParams = new URLSearchParams(window.location.hash.split("?")[1] || "");
                const providedChatId = urlParams.get("chatId") || hashParams.get("chatId");

                if (providedChatId) {
                    // Use existing chat ID
                    setCurrentChatId(providedChatId);
                    setInitialized(true);

                    // Notify parent window of resumed chat
                    if (window.parent !== window) {
                        window.parent.postMessage(
                            {
                                type: "cts-chat-update",
                                chatId: providedChatId,
                            },
                            "*",
                        );
                    }

                    // Load existing messages to get suggestions
                    const existingMessages = await db.messages
                        .where("chatId")
                        .equals(providedChatId)
                        .toArray();
                    if (existingMessages.length === 0) {
                        // If no messages, set default suggestions
                        setSuggestedMessages([
                            "What maintenance procedures exist for SGT-800?",
                            "Show me recent field service reports",
                            "Find troubleshooting guides for gas turbines",
                        ]);
                    }
                } else {
                    // Create new chat if no ID provided
                    const newChatId = uuidv4();
                    const newChat = ChatEntity._("combined")
                        .setId(newChatId)
                        .setDescription("Widget Chat");
                    await newChat.add();
                    setCurrentChatId(newChatId);
                    setInitialized(true);

                    // Notify parent window of chat ID
                    if (window.parent !== window) {
                        window.parent.postMessage(
                            {
                                type: "cts-chat-update",
                                chatId: newChatId,
                            },
                            "*",
                        );
                    }

                    // Set initial suggestions
                    setSuggestedMessages([
                        "What maintenance procedures exist for SGT-800?",
                        "Show me recent field service reports",
                        "Find troubleshooting guides for gas turbines",
                    ]);
                }

                // Check if authenticated before sending ready
                if (window.parent !== window) {
                    if (manualToken || (accounts && accounts.length > 0)) {
                        // User is authenticated, send ready message
                        window.parent.postMessage({ type: "cts-ready" }, "*");
                    } else {
                        // User is not authenticated, request token from parent first
                        window.parent.postMessage({ type: "cts-request-token-from-parent" }, "*");
                        // Wait a bit for response, then send auth required if no token received
                        setTimeout(() => {
                            if (!manualToken && (!accounts || accounts.length === 0)) {
                                window.parent.postMessage({ type: "cts-auth-required" }, "*");
                            }
                        }, 500);
                    }
                }
            } catch (error) {
                console.error("Error initializing chat:", error);
                const fallbackChatId = uuidv4();
                setCurrentChatId(fallbackChatId);
                setInitialized(true);

                // Notify parent window even on error
                if (window.parent !== window) {
                    window.parent.postMessage(
                        {
                            type: "cts-chat-update",
                            chatId: fallbackChatId,
                        },
                        "*",
                    );
                    // Check auth status even on error
                    if (manualToken || (accounts && accounts.length > 0)) {
                        window.parent.postMessage({ type: "cts-ready" }, "*");
                    } else {
                        window.parent.postMessage({ type: "cts-auth-required" }, "*");
                    }
                }
            }
        };

        // Always try to init if we're not initialized
        // OR if we just got authentication but don't have a chat yet
        if (!initialized || (!currentChatId && (manualToken || accounts.length > 0))) {
            initChat();
        }
    }, [initialized, accounts, manualToken, currentChatId]);

    // Auto-scroll to bottom
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, []);

    // Process streaming response
    const processStreamResponse = useCallback(
        async (
            response: Response,
            assistantMessageId: string,
        ): Promise<{ answer: string; answerJson: AnswerJson }> => {
            let answer = "";
            const answerJson: AnswerJson = {
                content: "",
                tool_call: false,
                references: [],
            };

            if (response.status === 403) {
                const errorData = await response.json();
                const errorMessage =
                    (Array.isArray(errorData.detail)
                        ? errorData.detail[0]?.msg
                        : errorData.detail) || "You are not authorized to access this resource.";
                throw new Error(errorMessage);
            }

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage =
                    (Array.isArray(errorData.detail)
                        ? errorData.detail[0]?.msg
                        : errorData.detail) || "An error occurred while processing your request";
                throw new Error(errorMessage);
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) {
                throw new Error("No response body");
            }

            setIsStreaming(true);

            // Notify parent that streaming has started
            if (window.parent !== window) {
                window.parent.postMessage({ type: "cts-streaming-start" }, "*");
            }

            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });
                    const lines = chunk.split("\n");

                    for (const line of lines) {
                        if (line.startsWith("data: ")) {
                            try {
                                const jsonValue = JSON.parse(line.slice(6)) as StreamEvent;

                                switch (jsonValue.event) {
                                    case "message":
                                        if (jsonValue.content) {
                                            answer += jsonValue.content;
                                            answerJson.content = answer;
                                            await db.messages.update(assistantMessageId, {
                                                content: answer,
                                            });

                                            // Send streaming chunk to parent
                                            if (window.parent !== window) {
                                                window.parent.postMessage(
                                                    {
                                                        type: "cts-streaming-chunk",
                                                        content: answer,
                                                    },
                                                    "*",
                                                );
                                            }
                                        }
                                        break;

                                    case "reference":
                                        if (jsonValue.content) {
                                            try {
                                                const parsedRef = parseContent(
                                                    jsonValue.content,
                                                ) as StreamedReference | null;
                                                const newReference: Reference = {
                                                    reference_id: parsedRef.reference_id,
                                                    title: parsedRef.title,
                                                    source: decodeURIComponent(parsedRef.source),
                                                    dataSourceType: parsedRef.data_source_type,
                                                    page_numbers: new Set<number>([
                                                        parsedRef.page_number,
                                                    ]),
                                                };
                                                const idx = answerJson.references.findIndex(
                                                    (r) =>
                                                        (r.reference_id &&
                                                            newReference.reference_id &&
                                                            r.reference_id ===
                                                                newReference.reference_id) ||
                                                        (r.source &&
                                                            newReference.source &&
                                                            r.source === newReference.source),
                                                );

                                                if (idx === -1) {
                                                    answerJson.references?.push(newReference);
                                                } else {
                                                    const existing = answerJson.references[idx];
                                                    for (const page of newReference.page_numbers) {
                                                        existing.page_numbers.add(page);
                                                    }
                                                }
                                                const currentMsg =
                                                    await db.messages.get(assistantMessageId);
                                                if (currentMsg) {
                                                    await db.messages.update(assistantMessageId, {
                                                        references: [
                                                            ...(currentMsg.references || []),
                                                            newReference,
                                                        ],
                                                    });
                                                }

                                                // Send reference to parent
                                                if (window.parent !== window) {
                                                    window.parent.postMessage(
                                                        {
                                                            type: "cts-streaming-chunk",
                                                            content: answer,
                                                            reference: newReference,
                                                        },
                                                        "*",
                                                    );
                                                }
                                            } catch (e) {
                                                console.error("Error parsing reference:", e);
                                            }
                                        }
                                        break;

                                    case "response_id":
                                        answerJson.response_id = jsonValue.content;
                                        await db.messages.update(assistantMessageId, {
                                            responseId: jsonValue.content,
                                        });
                                        break;

                                    case "on_tool_start": {
                                        answerJson.tool_call = true;
                                        const msgOnStart =
                                            await db.messages.get(assistantMessageId);
                                        if (msgOnStart) {
                                            const activities = [
                                                ...(msgOnStart.toolActivities ?? []),
                                            ];
                                            const reuseIdx = activities.findIndex(
                                                (a) =>
                                                    a.internalName === (jsonValue.content ?? "") &&
                                                    a.done,
                                            );
                                            if (reuseIdx !== -1) {
                                                activities[reuseIdx] = {
                                                    internalName: jsonValue.content ?? "",
                                                    done: false,
                                                };
                                            } else {
                                                activities.push({
                                                    internalName: jsonValue.content ?? "",
                                                    done: false,
                                                });
                                            }
                                            await db.messages.update(assistantMessageId, {
                                                toolCall: true,
                                                toolCallCount: (msgOnStart.toolCallCount ?? 0) + 1,
                                                toolActivities: activities,
                                            });
                                        }
                                        break;
                                    }

                                    case "on_tool_end":
                                    case "on_tool_error": {
                                        const isToolError = jsonValue.event === "on_tool_error";
                                        const msgOnEnd = await db.messages.get(assistantMessageId);
                                        if (msgOnEnd) {
                                            const activities = [...(msgOnEnd.toolActivities ?? [])];
                                            const idx = activities.findIndex(
                                                (a) =>
                                                    a.internalName === (jsonValue.content ?? "") &&
                                                    !a.done &&
                                                    !a.error,
                                            );
                                            if (idx !== -1)
                                                activities[idx] = {
                                                    ...activities[idx],
                                                    done: true,
                                                    error: isToolError,
                                                };
                                            const newCount = Math.max(
                                                0,
                                                (msgOnEnd.toolCallCount ?? 0) - 1,
                                            );
                                            await db.messages.update(assistantMessageId, {
                                                toolCallCount: newCount,
                                                toolCall: newCount > 0,
                                                toolActivities: activities,
                                            });
                                        }
                                        answerJson.tool_call = false;
                                        break;
                                    }

                                    case "error":
                                        throw new Error(jsonValue.content || "An error occurred");

                                    case "end":
                                        setIsStreaming(false);

                                        // Send completion message to parent
                                        if (window.parent !== window) {
                                            window.parent.postMessage(
                                                {
                                                    type: "cts-complete",
                                                    detail: {
                                                        answer: answer,
                                                        references: answerJson.references || [],
                                                        responseId: answerJson.response_id,
                                                    },
                                                },
                                                "*",
                                            );
                                        }
                                        break;

                                    case "suggestions":
                                        if (jsonValue.content) {
                                            try {
                                                const suggestions = JSON.parse(jsonValue.content);
                                                answerJson.suggestions = suggestions;
                                                setSuggestedMessages(suggestions);
                                            } catch (e) {
                                                console.error("Error parsing suggestions:", e);
                                            }
                                        }
                                        break;
                                }
                            } catch (_e) {
                                // Ignore JSON parse errors
                            }
                        }
                    }
                }
            } finally {
                setIsStreaming(false);
            }

            return { answer, answerJson };
        },
        [],
    );

    const handleSend = useCallback(
        async (messageText?: string) => {
            const textToSend = messageText || message.trim();
            if (!textToSend || !currentChatId || isLoading) return;

            // Check if user is authenticated (either via MSAL or manual token)
            if (!manualToken && (!accounts || accounts.length === 0)) {
                // Send auth required message to parent
                if (window.parent !== window) {
                    window.parent.postMessage({ type: "cts-auth-required" }, "*");
                }
                return;
            }

            // Prevent multiple concurrent requests
            if (isStreaming) {
                return;
            }

            if (!messageText) {
                setMessage("");
            }
            setIsLoading(true);
            setSuggestedMessages([]);

            if (abortControllerRef.current?.signal && !abortControllerRef.current.signal.aborted) {
                abortControllerRef.current.abort("New request started");
            }

            abortControllerRef.current = new AbortController();

            try {
                // Add user message
                const userMessageId = nanoid();
                const userMessage = MessageEntity._();
                userMessage
                    .setId(userMessageId)
                    .setChatId(currentChatId)
                    .setContent(textToSend)
                    .setRole(ChatRole.USER);
                userMessage.done = true;

                await userMessage.add();

                // Add assistant placeholder
                const assistantMessageId = nanoid();
                const assistantMessage = MessageEntity._();
                assistantMessage
                    .setId(assistantMessageId)
                    .setChatId(currentChatId)
                    .setContent("")
                    .setRole(ChatRole.ASSISTANT)
                    .setRepliedId(userMessageId);
                assistantMessage.done = false;
                assistantMessage.references = [];
                assistantMessage.toolCall = false;

                await assistantMessage.add();

                // Get access token
                let accessToken: string | null;
                try {
                    accessToken = await getAccessToken();
                } catch (tokenError) {
                    console.error("Failed to get access token:", tokenError);
                    throw tokenError;
                }

                // Prepare payload
                const payload = {
                    question: textToSend,
                    conversation_id: currentChatId,
                    options: {
                        debug: false,
                        memory_enabled: true,
                        metadata_filter: [],
                        product_line:
                            chatPreferences.selectedProductLines.length > 0
                                ? chatPreferences.selectedProductLines.join(",")
                                : "all",
                        datasources:
                            chatPreferences.selectedDatasources.length > 0
                                ? chatPreferences.selectedDatasources
                                : [],
                        organization: selectedOrganization || undefined,
                    },
                };

                // Make API request
                const backendUrl =
                    import.meta.env.VITE_BACKEND_URL ||
                    "https://api.ads-deep.mosaic-qa.siemens-energy.cloud";
                const response = await fetch(`${backendUrl}/v1/chat/stream`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                        "X-Transaction-ID": uuidv4(),
                        Accept: "text/event-stream",
                    },
                    body: JSON.stringify(payload),
                    signal: abortControllerRef.current?.signal,
                    cache: "no-cache",
                    keepalive: true,
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("API request failed:", response.status, errorText);
                    throw new Error(`API request failed: ${response.status}`);
                }

                // Process streaming response
                const { answer, answerJson } = await processStreamResponse(
                    response,
                    assistantMessageId,
                );

                // Mark as done
                await db.messages.update(assistantMessageId, {
                    done: true,
                    content: answer || "No response received",
                    responseId: answerJson.response_id,
                });

                // Update suggestions
                if (answerJson.suggestions && answerJson.suggestions.length > 0) {
                    setSuggestedMessages(answerJson.suggestions);
                }

                // Send completion event
                window.parent.postMessage(
                    {
                        type: "cts-search-complete",
                        results: {
                            query: textToSend,
                            response: answer,
                            references: answerJson.references,
                            responseId: answerJson.response_id,
                        },
                    },
                    "*",
                );
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : "An error occurred";

                // Don't treat abort as an error if it's for a new request
                if (error.name === "AbortError" && errorMessage === "New request started") {
                    // This is expected when starting a new request, not an error
                    return;
                }

                console.error("Error:", error);

                // Send error message to parent
                if (window.parent !== window) {
                    window.parent.postMessage(
                        {
                            type: "cts-error",
                            detail: {
                                message: errorMessage,
                            },
                        },
                        "*",
                    );
                }

                if (error.name !== "AbortError") {
                    // Check if it's an authentication error
                    if (
                        errorMessage.includes("No account found") ||
                        errorMessage.includes("not authenticated")
                    ) {
                        // Send auth required instead of error
                        if (window.parent !== window) {
                            window.parent.postMessage({ type: "cts-auth-required" }, "*");
                        }
                        return;
                    }

                    const messages = await db.messages
                        .where("chatId")
                        .equals(currentChatId)
                        .toArray();
                    const lastAssistantMessage = messages
                        .filter((m) => m.role === "assistant")
                        .sort((a, b) => b.createdAt - a.createdAt)[0];

                    if (lastAssistantMessage) {
                        const errorMessage =
                            error.message || "An error occurred. Please try again.";

                        await db.messages.update(lastAssistantMessage.id, {
                            content: errorMessage,
                            done: true,
                            hasError: true,
                            error: errorMessage,
                        });
                    }
                }
            } finally {
                setIsLoading(false);
                setIsStreaming(false);
                abortControllerRef.current = null;
            }
        },
        [
            message,
            currentChatId,
            isLoading,
            isStreaming,
            chatPreferences,
            selectedOrganization,
            getAccessToken,
            processStreamResponse,
            accounts,
            manualToken,
        ],
    );

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Listen for postMessage commands
    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            if (event.data?.type === "cts-command") {
                if (event.data.action === "search" && event.data.query) {
                    setMessage(event.data.query);
                    setTimeout(() => handleSend(event.data.query), 100);
                } else if (event.data.action === "clear") {
                    // Clear the chat
                    setMessage("");
                }
            } else if (event.data?.type === "cts-auto-submit") {
                // Auto-submit question from parent widget
                if (event.data.question) {
                    setMessage(event.data.question);
                    // Check if chat is initialized before sending
                    const attemptSend = () => {
                        if (currentChatId) {
                            handleSend(event.data.question);
                        } else {
                            setTimeout(attemptSend, 1000);
                        }
                    };
                    // Give time for initialization
                    setTimeout(attemptSend, 1500);
                }
            } else if (event.data?.type === "cts-auth-token") {
                // Handle token from parent web component
                if (event.data.token) {
                    setManualToken(event.data.token);

                    // Extract user info from token if possible (JWT decode)
                    try {
                        const tokenParts = event.data.token.split(".");
                        if (tokenParts.length === 3) {
                            const payload = JSON.parse(atob(tokenParts[1]));
                            setManualAccount({
                                name: payload.name || payload.preferred_username || "User",
                            });
                        }
                    } catch (_e) {
                        setManualAccount({ name: "User" });
                    }

                    // Force re-initialization if we don't have a chat ID yet
                    if (!currentChatId) {
                        setInitialized(false);
                        // This will trigger the useEffect to create a chat
                    }

                    // Send ready message to parent
                    window.parent.postMessage({ type: "cts-ready" }, "*");

                    // Store the pending question to submit after chat initialization
                    if (event.data.question) {
                        // Wait longer for chat to initialize after token is set
                        setTimeout(() => {
                            if (currentChatId) {
                                handleSend(event.data.question);
                            } else {
                                // Try again after another delay
                                setTimeout(() => handleSend(event.data.question), 2000);
                            }
                        }, 1500);
                    }
                }
            } else if (event.data?.type === "cts-auth-trigger") {
                try {
                    // Check if already authenticated via manual token
                    if (manualToken) {
                        window.parent.postMessage({ type: "cts-ready" }, "*");
                    } else if (accounts.length === 0) {
                        // Try to trigger authentication
                        const response = await instance.loginPopup({
                            scopes: scopesNeeded,
                            prompt: "select_account",
                        });
                        if (response.account) {
                            // Authentication successful, send ready message
                            window.parent.postMessage({ type: "cts-ready" }, "*");
                        }
                    } else {
                        window.parent.postMessage({ type: "cts-ready" }, "*");
                    }
                } catch (error) {
                    console.error("Authentication failed:", error);
                    window.parent.postMessage(
                        {
                            type: "cts-error",
                            detail: {
                                message:
                                    error instanceof Error
                                        ? error.message
                                        : "Authentication failed",
                            },
                        },
                        "*",
                    );
                }
            } else if (event.data?.type === "cts-request-token") {
                // Handle token request
                try {
                    const token = await getAccessToken();
                    // Send token response back
                    window.parent.postMessage(
                        {
                            type: "cts-token-response",
                            token: token,
                            requestId: event.data.requestId,
                        },
                        "*",
                    );
                } catch (error) {
                    console.error("Failed to get token:", error);
                    window.parent.postMessage(
                        {
                            type: "cts-token-response",
                            token: null,
                            error: error instanceof Error ? error.message : "Failed to get token",
                            requestId: event.data.requestId,
                        },
                        "*",
                    );
                }
            } else if (event.data?.type === "cts-ping") {
                window.parent.postMessage({ type: "cts-pong" }, "*");
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [handleSend, getAccessToken, instance, accounts, currentChatId, manualToken]);

    const toggleReferences = useCallback((messageId: string) => {
        setExpandedReferences((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(messageId)) {
                newSet.delete(messageId);
            } else {
                newSet.add(messageId);
            }
            return newSet;
        });
    }, []);

    // Handle clicks on inline references
    useEffect(() => {
        const handleReferenceClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains("inline-reference")) {
                e.preventDefault();
                const refId = target.getAttribute("data-ref-id");
                if (refId && messages) {
                    // Find the reference from all messages
                    let foundRef: Reference | null = null;
                    for (const msg of messages) {
                        if (msg.references) {
                            const ref = msg.references.find((r) => r.reference_id === refId);
                            if (ref) {
                                foundRef = ref;
                                break;
                            }
                        }
                    }

                    if (foundRef) {
                        setSelectedReference(foundRef);
                    }

                    // Also expand the references section for the message
                    const messageElement = target.closest(".message-wrapper");
                    const messageId = messageElement?.getAttribute("data-message-id");
                    if (messageId) {
                        toggleReferences(messageId);
                    }
                }
            }
        };

        document.addEventListener("click", handleReferenceClick);
        return () => document.removeEventListener("click", handleReferenceClick);
    }, [toggleReferences, messages]);

    if (!initialized) {
        return (
            <div className="widget-chat-loading">
                <div className="loading-spinner" />
                <div className="loading-text">Initializing chat...</div>
            </div>
        );
    }

    return (
        <div className="widget-chat-container">
            {/* Header */}
            <div className="widget-chat-header">
                <div className="header-title">
                    <RobotIcon />
                    <span>CTS Knowledge Assistant</span>
                </div>
                {chatPreferences.selectedProductLines.length > 0 && (
                    <div className="header-filters">
                        {chatPreferences.selectedProductLines.map((pl) => (
                            <span key={pl} className="filter-badge">
                                {pl}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Messages Area */}
            <div className="widget-chat-messages" ref={scrollRef}>
                {(!messages || messages.length === 0) && (
                    <div className="welcome-container">
                        <div className="welcome-icon">
                            <RobotIcon />
                        </div>
                        <h2 className="welcome-title">Welcome to Knowledge Search</h2>
                        <p className="welcome-subtitle">
                            I can help you find technical documentation, maintenance procedures,
                            field reports, and troubleshooting guides.
                        </p>
                        {suggestedMessages.length > 0 && (
                            <div className="welcome-suggestions">
                                <p className="suggestions-label">Try asking:</p>
                                {suggestedMessages.map((suggestion, _idx) => (
                                    <button
                                        type="button"
                                        key={suggestion}
                                        className="suggestion-button"
                                        onClick={() => {
                                            setMessage(suggestion);
                                            handleSend(suggestion);
                                        }}
                                        disabled={false}
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {messages?.map((msg) => (
                    <div
                        key={String(msg.id)}
                        className={`message-wrapper ${msg.role}`}
                        data-message-id={msg.id}
                    >
                        <div className="message-content">
                            {msg.role === "assistant" && (
                                <div className="message-avatar assistant">
                                    <RobotIcon />
                                </div>
                            )}

                            <div className="message-bubble">
                                {(msg.toolCall || (msg.toolActivities?.length ?? 0) > 0) &&
                                    !msg.done &&
                                    msg.role === "assistant" && (
                                        <div className="tool-activities">
                                            <div className="typing-dots tool-activities-dots">
                                                <span />
                                                <span />
                                                <span />
                                            </div>
                                            <span className="tool-activities-label">
                                                Looking into internal knowledge bases
                                            </span>
                                            {(msg.toolActivities?.length ?? 0) === 0 ? (
                                                <svg
                                                    width="14"
                                                    height="14"
                                                    viewBox="0 0 14 14"
                                                    fill="none"
                                                    className="tool-spinner"
                                                    role="img"
                                                >
                                                    <title>In progress</title>
                                                    <circle
                                                        cx="7"
                                                        cy="7"
                                                        r="5.5"
                                                        stroke="#8A00E5"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeDasharray="8 26"
                                                    />
                                                </svg>
                                            ) : (
                                                (msg.toolActivities ?? []).map((activity, i) => {
                                                    const key = `${activity.internalName}-${i}`;
                                                    if (!activity.done)
                                                        return (
                                                            <svg
                                                                key={key}
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 14 14"
                                                                fill="none"
                                                                className="tool-spinner"
                                                                role="img"
                                                            >
                                                                <title>In progress</title>
                                                                <circle
                                                                    cx="7"
                                                                    cy="7"
                                                                    r="5.5"
                                                                    stroke="#8A00E5"
                                                                    strokeWidth="1.5"
                                                                    strokeLinecap="round"
                                                                    strokeDasharray="8 26"
                                                                />
                                                            </svg>
                                                        );
                                                    if (activity.error)
                                                        return (
                                                            <svg
                                                                key={key}
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 14 14"
                                                                fill="none"
                                                                role="img"
                                                            >
                                                                <title>Error</title>
                                                                <path
                                                                    d="M3 3l8 8M11 3l-8 8"
                                                                    stroke="#e03131"
                                                                    strokeWidth="1.5"
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        );
                                                    return (
                                                        <svg
                                                            key={key}
                                                            width="14"
                                                            height="14"
                                                            viewBox="0 0 14 14"
                                                            fill="none"
                                                            role="img"
                                                        >
                                                            <title>Done</title>
                                                            <circle
                                                                cx="7"
                                                                cy="7"
                                                                r="6.5"
                                                                stroke="#9ca3af"
                                                            />
                                                            <path
                                                                d="M4 7l2 2 4-4"
                                                                stroke="#9ca3af"
                                                                strokeWidth="1.5"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}

                                <div
                                    className="message-text"
                                    // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized in renderMarkdown
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            msg.role === "assistant" && msg.content
                                                ? renderMarkdown(msg.content, msg.references)
                                                : msg.content ||
                                                  (msg.role === "assistant" && !msg.done
                                                      ? "Thinking..."
                                                      : ""),
                                    }}
                                />

                                {!msg.done && msg.role === "assistant" && isStreaming && (
                                    <div className="streaming-indicator">
                                        <div className="typing-dots">
                                            <span />
                                            <span />
                                            <span />
                                        </div>
                                    </div>
                                )}

                                {/* References */}
                                {msg.references &&
                                    msg.references.length > 0 &&
                                    msg.role === "assistant" && (
                                        <div className="references-container">
                                            <button
                                                type="button"
                                                className="references-toggle"
                                                onClick={() => toggleReferences(String(msg.id))}
                                            >
                                                <LinkIcon />
                                                <span>
                                                    {msg.references.length} source
                                                    {msg.references.length > 1 ? "s" : ""}
                                                </span>
                                                <ChevronIcon
                                                    isOpen={expandedReferences.has(String(msg.id))}
                                                />
                                            </button>

                                            {expandedReferences.has(String(msg.id)) && (
                                                <div className="references-list">
                                                    {msg.references.map((ref, idx) => (
                                                        <div
                                                            key={ref.reference_id || `ref-${idx}`}
                                                            className="reference-item"
                                                        >
                                                            <span className="reference-title">
                                                                {ref.title || ref.source}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                            </div>

                            {msg.role === "user" && (
                                <div className="message-avatar user">
                                    <UserIcon />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Suggested messages */}
            {messages && messages.length > 0 && suggestedMessages.length > 0 && !isLoading && (
                <div className="suggestions-container">
                    <SparklesIcon />
                    <span className="suggestions-label">Suggested:</span>
                    {suggestedMessages.map((suggestion, _idx) => (
                        <button
                            type="button"
                            key={suggestion}
                            className="suggestion-chip"
                            onClick={() => {
                                setMessage(suggestion);
                                handleSend(suggestion);
                            }}
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            )}

            {/* Input Area */}
            <div className="widget-chat-input">
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about technical documentation, procedures, or troubleshooting..."
                    disabled={isLoading}
                    className="chat-textarea"
                    rows={1}
                />
                <button
                    type="button"
                    onClick={() => handleSend()}
                    disabled={!message.trim() || isLoading}
                    className={`send-button ${isLoading ? "loading" : ""}`}
                >
                    {isLoading ? <span className="button-spinner" /> : <SendIcon />}
                </button>
            </div>

            {/* Reference Modal */}
            {selectedReference && (
                <div
                    role="button"
                    tabIndex={0}
                    className="reference-modal-overlay"
                    onClick={() => setSelectedReference(null)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape" || e.key === "Enter") setSelectedReference(null);
                    }}
                    aria-label="Close modal overlay"
                >
                    <div
                        role="dialog"
                        className="reference-modal"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        aria-modal="true"
                    >
                        <div className="reference-modal-header">
                            <h3>Reference Details</h3>
                            <button
                                type="button"
                                className="reference-modal-close"
                                onClick={() => setSelectedReference(null)}
                                aria-label="Close"
                            >
                                <CloseIcon />
                            </button>
                        </div>
                        <div className="reference-modal-content">
                            <div className="reference-field">
                                <span className="reference-label">Title:</span>
                                <span className="reference-value">
                                    {selectedReference.title || "No title"}
                                </span>
                            </div>
                            <div className="reference-field">
                                <span className="reference-label">Source:</span>
                                <span className="reference-value">
                                    {selectedReference.source || "No source"}
                                </span>
                            </div>
                            {selectedReference.dataSourceType && (
                                <div className="reference-field">
                                    <span className="reference-label">Type:</span>
                                    <span className="reference-value">
                                        {selectedReference.dataSourceType}
                                    </span>
                                </div>
                            )}
                            <div className="reference-field">
                                <span className="reference-label">Reference ID:</span>
                                <span className="reference-value">
                                    {selectedReference.reference_id}
                                </span>
                            </div>

                            {selectedReference.source && (
                                <div className="reference-actions">
                                    <button
                                        type="button"
                                        className="reference-action-button"
                                        onClick={async () => {
                                            const fullUrl = getFullReferenceUrl(
                                                selectedReference.source,
                                            );

                                            // For files that need authentication, open in main app
                                            if (
                                                fullUrl.includes("api.ads-deep.mosaic") ||
                                                fullUrl.includes("/v1/storage/")
                                            ) {
                                                // Get access token for authentication
                                                const accessToken = await getAccessToken();

                                                // Open in main app with proper auth context
                                                const mainAppUrl = window.location.origin;
                                                const referenceViewUrl = `${mainAppUrl}?viewReference=${encodeURIComponent(fullUrl)}`;
                                                window.open(referenceViewUrl, "_blank");

                                                // Also send message to parent window if in iframe
                                                window.parent.postMessage(
                                                    {
                                                        type: "cts-view-reference",
                                                        referenceUrl: fullUrl,
                                                        referenceId: selectedReference.reference_id,
                                                        token: accessToken,
                                                    },
                                                    "*",
                                                );
                                            } else {
                                                // For regular URLs, just open directly
                                                window.open(fullUrl, "_blank");
                                            }
                                        }}
                                    >
                                        <ExternalLinkIcon />
                                        <span>View in App</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
